import React, { useContext, useEffect, useRef, useState, ChangeEvent } from "react";
import { Container, Table, Tabs, Tab, Button, Form } from "react-bootstrap";
import { Navigate } from 'react-router-dom';

import { DrinkUpServiceApiContext } from "../DrinkUpServiceApiContext";
import LoadingScreen from "../components/LoadingScreen";
import RumRequestModal from "../components/AdminPage/RumRequestModal";
import { MEMBER_USER, UserContext } from '../hooks/useUser';
import { Paths } from'../pages';
import AdminNav from "../components/AdminPage/AdminNav";
import AdminUserModal from "../components/AdminPage/AdminUserModal";
import AdminRumModel from "../components/AdminPage/AdminRumModal";
import { AdminRum, AdminUser, RumRequest, SortField } from '../types';

const SELECTED_REQUEST_IDS_STORAGE_KEY = "adminPage.selectedRequestIds";

function readSelectedRequestIdsFromStorage(): Set<number> {
    try {
        const raw = localStorage.getItem(SELECTED_REQUEST_IDS_STORAGE_KEY);
        if (!raw) {
            return new Set();
        }
        const parsed = JSON.parse(raw) as unknown;
        if (!Array.isArray(parsed)) {
            return new Set();
        }
        const ids = parsed.filter((x): x is number => typeof x === "number" && Number.isInteger(x));
        return new Set(ids);
    } catch {
        return new Set();
    }
}

function AdminPage(): JSX.Element {

    const { authToken, userDetails } = useContext(UserContext);
    const { drinkUpService } = useContext(DrinkUpServiceApiContext);

    const [requestShown, setRequestShown] = useState<boolean>(false);
    const [isDoneLoading, setIsDoneLoading] = useState<boolean>(false);
    const [userShown, setUserShown] = useState<boolean>(false);
    const [rumShown, setRumShown] = useState<boolean>(false);
    const [filteredRums, setFilteredRums] = useState<AdminRum[]>([]);
    const [filteredUsers, setFilteredUsers] = useState<AdminUser[]>([]);
    const [sortedRequests, setSortedRequests] = useState<RumRequest[]>([]);
    const [selectedRequestIds, setSelectedRequestIds] = useState<Set<number>>(readSelectedRequestIdsFromStorage);

    const isLoaded = useRef<boolean>(false);
    const requests = useRef<RumRequest[]>([]);
    const rums = useRef<AdminRum[]>([]);
    const users = useRef<AdminUser[]>([]);
    const selectedRequest = useRef<RumRequest | null>(null);
    const selectedRum = useRef<AdminRum | null>(null);
    const selectedUser = useRef<AdminUser | null>(null);
    const searchText = useRef<string>("");
    const rumSortField = useRef<SortField | "">("");
    const rumSortAscending = useRef<boolean>(false);
    const userSortField = useRef<SortField | "">("");
    const userSortAscending = useRef<boolean>(false);

    useEffect(() => {
        try {
            localStorage.setItem(
                SELECTED_REQUEST_IDS_STORAGE_KEY,
                JSON.stringify(Array.from(selectedRequestIds))
            );
        } catch {
            // ignore quota / private mode
        }
    }, [selectedRequestIds]);

    useEffect(() => {
        if (sortedRequests.length === 0) {
            return;
        }
        const validIds = new Set(sortedRequests.map((r) => r.id));
        setSelectedRequestIds((prev) => {
            const next = new Set<number>();
            Array.from(prev).forEach((id) => {
                if (validIds.has(id)) {
                    next.add(id);
                }
            });
            const prevArr = Array.from(prev);
            if (next.size === prev.size && prevArr.every((id) => next.has(id))) {
                return prev;
            }
            return next;
        });
    }, [sortedRequests]);

    if (!authToken) {
        console.log("no auth token for admin page");
        return <Navigate to={Paths.root} replace={true} />;
    } 
    if (!userDetails) {
        console.log("admin: no user details");
        return <Navigate to={Paths.root} replace={true} />;
    }

    if (userDetails.type === MEMBER_USER) {
        console.log("not admin or manager");
        return <Navigate to={Paths.root} replace={true} />;
    }    

    const rumNameFor = (rumId: number): string => {
        const filtered = rums.current.filter(rum => rum.id === rumId);
        if (!filtered[0]) {
            return "TBD";
        }
        return filtered[0].bevager_name;
    }

    const rumInventoryIdFor = (rumId: number): string => {
        const filtered = rums.current.filter(rum => rum.id === rumId);
        if (!filtered[0]) {
            return "TBD";
        }
        return filtered[0].inventory_id ?? "TBD";
    }

    function usernameFor(userId: number): string {
        const filtered = users.current.filter(user => user.user === userId);
        if (!filtered[0]) {
            return "TBD";
        }
        return filtered[0].first_name + " " + filtered[0].last_name;
    }

    function formattedDateTime(dateString: string): string {
        const date = new Date(Date.parse(dateString));

        let ampm = "AM";

        let hours = date.getHours();
        if (hours > 12) {
            hours = hours - 12;
            ampm = "PM";
        } else if (hours === 0) {
            hours = 12;
        } else if (hours === 12) {
            ampm = "PM";
        }

        return date.toDateString() + ", " + hours + ":" + String(date.getMinutes()).padStart(2, '0') + " " + ampm;
    }

    const loadUsers = (): void => {
        console.log("loading users");
        drinkUpService.get<AdminUser[]>("/get_users", {
            headers: {
                'Authorization': `Token ${authToken}`,
                'Content-Type': 'application/json'
            }
        })
        .then(response => {
            console.log("num users: " + response.data.length);
            console.log("users: " + JSON.stringify(response.data));
            users.current = response.data;
            filterItems();
            setIsDoneLoading(true);
        })
        .catch(err => {
            console.log(err);
        });
    }

    const loadRums = (): void => {
        console.log("loading rums");

        drinkUpService.get<AdminRum[]>("/get_rums", {
            headers: {
                'Authorization': `Token ${authToken}`,
                'Content-Type': 'application/json'
            }
        })
        .then(response => {
            console.log("num rums: " + response.data.length);
            rums.current = response.data;
            setFilteredRums(rums.current);
            loadUsers();
        })
        .catch(err => {
            console.log(err);
        });
    }

    const sortRequests = (): void => {
        //sort requests
        const sorted: RumRequest[] = [];

        requests.current.forEach((request, index, arr) => {
            
            //don't add already responded items
            if(!!request.responded_by) {
                return;
            }

            //check if item already added
            if(sorted.some( req => req.id === request.id )) {
                return;
            }

            sorted.push(request);
            
            let i = index + 1;

            const date1 = Date.parse(request.requested_on);

            while(i < arr.length) {
                //check if same user
                if(arr[i].requested_by === request.requested_by && !arr[i].responded_by) {
                    ///check if time is within 15 hours
                    const date2 = Date.parse(arr[i].requested_on);
                    const timeDiff = (date1 - date2) / 1000;

                    if((timeDiff / 3600) < 15) {
                        sorted.push(arr[i]);
                    }
                }

                i++;
            }
        });

        setSortedRequests(sorted);
    }

    const loadRequests = (): void => {
        
        console.log("loading rum requests");

        drinkUpService.get<RumRequest[]>("/get_user_rums", {
            headers: {
                'Authorization': `Token ${authToken}`,
                'Content-Type': 'application/json'
            }
        })
        .then(response => {
            console.log("num rum requests: " + response.data.length);
            console.log("requests: " + JSON.stringify(response.data));
            requests.current = response.data;

            // sort rum requests
            sortRequests();

            loadRums();
        })
        .catch(err => {
            console.log(err);
        });
    }

    function sortRums(sortField: SortField): void {
        if(rumSortField.current === sortField) {
            rumSortAscending.current = !rumSortAscending.current;
        }
        rumSortField.current = sortField;
        filterItems();
    } 

    function sortUsers(sortField: SortField): void {
        if(userSortField.current === sortField) {
            userSortAscending.current = !userSortAscending.current;
        }
        userSortField.current = sortField;
        filterItems();
    }

    function compareRums(a: AdminRum, b: AdminRum): number {
        if(rumSortField.current === "name") {
            const sorted = a.bevager_name.toUpperCase() < b.bevager_name.toUpperCase();
            if(rumSortAscending.current) {
                return sorted ? 1 : -1;
            } else {
                return sorted ? -1 : 1;
            }
        } else if(rumSortField.current === "inventory_id") {
            const aId = (a.inventory_id ?? "").toUpperCase();
            const bId = (b.inventory_id ?? "").toUpperCase();
            const sorted = aId < bId;
            if(rumSortAscending.current) {
                return sorted ? 1 : -1;
            } else {
                return sorted ? -1 : 1;
            }
        } else if(rumSortField.current === "price") {
            const sorted = a.price < b.price;
            if(rumSortAscending.current) {
                return sorted ? 1 : -1;
            } else {
                return sorted ? -1 : 1;
            }
        } else if(rumSortField.current === "available") {
            if(a.is_available === rumSortAscending.current) {
                return -1;
            }
            return 1;
        } else if(rumSortField.current === "date_added") {
            const sorted = a.date_added < b.date_added;
            if(rumSortAscending.current) {
                return sorted ? 1 : -1;
            } else {
                return sorted ? -1 : 1;
            }
        }
        return 0;
    }

    function compareUsers(a: AdminUser, b: AdminUser): number {
        if(userSortField.current === "name") {
            const sorted = a.last_name.toUpperCase() < b.last_name.toUpperCase();
            if(userSortAscending.current) {
                return sorted ? 1 : -1;
            } else {
                return sorted ? -1 : 1;
            }
        } else if(userSortField.current === "email") {
            const sorted = (a.email ?? "").toUpperCase() < (b.email ?? "").toUpperCase();
            if(userSortAscending.current) {
                return sorted ? 1 : -1;
            } else {
                return sorted ? -1 : 1;
            }
        } else if(userSortField.current === "num_rums") {
            const sorted = a.num_rums < b.num_rums;
            if(userSortAscending.current) {
                return sorted ? 1 : -1;
            } else {
                return sorted ? -1 : 1;
            }
        } else if(userSortField.current === "num_immortals") {
            const sorted = a.num_immortals < b.num_immortals;
            if(userSortAscending.current) {
                return sorted ? 1 : -1;
            } else {
                return sorted ? -1 : 1;
            }
        } else if(userSortField.current === "level") {
            const sorted = a.rumbustion_level < b.rumbustion_level;
            if(userSortAscending.current) {
                return sorted ? 1 : -1;
            } else {
                return sorted ? -1 : 1;
            }
        }
        return 0;
    }

    function filterRum(rum: AdminRum): boolean {
        if(!searchText.current) {
            return true;
        }

        const terms = searchText.current.split(" ");

        for(const term of terms) {
            if (rum.origin_country) {
                if(rum.origin_country.toUpperCase().includes(term.toUpperCase())) {
                    continue;
                }
            }

            if(rum.bevager_name.toUpperCase().includes(term.toUpperCase())) {
                continue;
            }

            if ((rum.inventory_id ?? "").toUpperCase().includes(term.toUpperCase())) {
                continue;
            }

            return false;
        }

        return true;
    }

    function filterUser(user: AdminUser): boolean {

        if(user.user_type !== "U") {
            return false;
        }

        if(!searchText.current) {
            return true;
        }

        const terms = searchText.current.split(" ");

        for(const term of terms) {
            if(user.first_name && user.first_name.toUpperCase().includes(term.toUpperCase())) {
                continue;
            }
            if(user.email && user.email.toUpperCase().includes(term.toUpperCase())) {
                continue;
            }
            if(user.last_name && user.last_name.toUpperCase().includes(term.toUpperCase())) {
                continue;
            }

            return false;
        }

        return true;
    }

    const filterItems = (): void => {
        const rum2 = [...rums.current];
        const filtered = rum2.filter(filterRum);
        filtered.sort(compareRums);
        setFilteredRums(filtered);

        const user2 = [...users.current];
        const filteredU = user2.filter(filterUser);
        filteredU.sort(compareUsers);
        setFilteredUsers(filteredU);
    }

    const closeModal = (): void => {
        selectedUser.current = null;
        if(requestShown) {
            setRequestShown(false);
        }
        if(rumShown) {
            setRumShown(false);
        }
        if(userShown) {
            setUserShown(false);
        }
        
        console.log("close modal");
    }

    const updateRum = (rum: any): void => {
        drinkUpService.post<AdminRum>("/update-rum", rum, {
            headers: {
                'Authorization': `Token ${authToken}`,
                'Content-Type': 'application/json'
            }
        })
        .then(response => {
            console.log("response: " + JSON.stringify(response.data));

            const index = rums.current.findIndex(function (item, i) {
                return item.id === response.data.id;
            });

            if(index >= 0) {
                rums.current[index] = response.data;
            } else {
                rums.current.push(response.data);
            }
            
            selectedRum.current = response.data;
            filterItems();
            closeModal();
        })
        .catch(err => {
            console.log("failed to update rum");
            console.log(err);
            alert("Failed to update rum :(");
        });
    }

    const respond = (rumId: number, requestId: number | string, approved: boolean, rejectionReason?: string): void => {
        const body = { rum: rumId, requestId: requestId, approved: approved, rejectionReason: rejectionReason };
        
        console.log("request: " + JSON.stringify(body));
        
        drinkUpService.post<RumRequest | RumRequest[]>("/request-rum", body, {
            headers: {
                'Authorization': `Token ${authToken}`,
                'Content-Type': 'application/json'
            }
        })
        .then(response => {
            console.log("response: " + JSON.stringify(response.data));
            const updatedRequests = Array.isArray(response.data) ? response.data : [response.data];

            updatedRequests.forEach(updatedRequest => {
                const requestIndex = requests.current.findIndex(r => r.id === updatedRequest.id);
                if (requestIndex >= 0) {
                    requests.current[requestIndex] = updatedRequest;
                }
            });
            
            sortRequests();
            setSelectedRequestIds(new Set());
            setRequestShown(false);
        })
        .catch(err => {
            console.log(err);
        });
    }

    if (!isLoaded.current) {
        isLoaded.current = true;
        loadRequests();
    }

    if (!isDoneLoading) {
        return <LoadingScreen />
    }

    const showRequest = (rowNumber: number): void => {
        console.log("Show rum: " + JSON.stringify(sortedRequests[rowNumber]));
        selectedRequest.current = sortedRequests[rowNumber];
        console.log(selectedRequest.current);
        const rum = rums.current.filter(r => r.id === selectedRequest.current!.rum)[0];
        console.log(rum);
        selectedRum.current = rum;
        selectedUser.current = users.current.filter(user => user.user === selectedRequest.current!.requested_by)[0];
        setRequestShown(true);
    }

    const showUser = (index: number): void => {
        const user = filteredUsers[index];
        console.log("Show user" + JSON.stringify(user));
        selectedUser.current = user;
        setUserShown(true);
    }

    const addNewRum = (): void => {

        const newRum: Partial<AdminRum> = {
            bevager_name: "",
            raw_material: "",
            is_independent_bottler: false,
            is_event: false,
            is_smugglers_cove_private_bottling: false,
            pour_size: "1",
            is_migrated: false,
            is_immortal: false,
            is_shown: true,
            is_available: true,
            price: 12,
            level_limit: 2
        };

        selectedRum.current = newRum as AdminRum;
        setRumShown(true);
    }

    const showRum = (index: number): void => {
        const rum = filteredRums[index];
        console.log("Show rum" + JSON.stringify(rum));
        selectedRum.current = rum;
        setRumShown(true);
    }

    const searchItems = (event: ChangeEvent<HTMLInputElement>): void => {
        console.log("Search Text: " + event.target.value);
        searchText.current = event.target.value;
        filterItems()
    }

    const handleCheckboxChange = (requestId: number, event: React.ChangeEvent<HTMLInputElement>): void => {
        event.stopPropagation();
        const newSelected = new Set(selectedRequestIds);
        if (event.target.checked) {
            newSelected.add(requestId);
        } else {
            newSelected.delete(requestId);
        }
        setSelectedRequestIds(newSelected);
    }

    const handleApproveAll = (): void => {
        if (selectedRequestIds.size === 0) {
            return;
        }

        const requestIdsArray = Array.from(selectedRequestIds);
        const requestIdsString = requestIdsArray.join(',');
        
        // For bulk approval, we don't need a specific rum ID, so we'll use 0 or empty
        // The backend should handle this based on the requestId parameter
        respond(0, requestIdsString, true);
    }

   const requestTabTitle = `Open requests (${sortedRequests.length})`;

    return (
        <Container fluid data-bs-theme="dark" data-theme="dark" className="text-light" id="body">
            <RumRequestModal show={requestShown} closeModal={closeModal} request={selectedRequest.current} user={selectedUser.current} rum={selectedRum.current} respond={respond} />
            <AdminUserModal show={userShown} closeModal={closeModal} user={selectedUser.current} rums={rums.current} />
            <AdminRumModel show={rumShown} closeModal={closeModal} rum={selectedRum.current} save={updateRum} />
            <AdminNav onSearchChange={searchItems} />
            <Tabs defaultActiveKey="requests" id="uncontrolled-tab-example" fill>
                <Tab eventKey="requests" title={requestTabTitle} >
                    <Button 
                        variant="primary" 
                        onClick={handleApproveAll}
                        disabled={selectedRequestIds.size === 0}
                        style={{ marginBottom: '10px' }}
                    >
                        Approve all
                    </Button>
                    <Table className="text-white table-fixed table table-responsive">
                        <thead>
                            <tr>
                                <th></th>
                                <th>Requester</th>
                                <th>RumID</th>
                                <th>Rum</th>
                                <th>Requested</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sortedRequests.map(function (request, i) {
                                return (
                                    <tr key={i} onClick={() => showRequest(i)}>
                                        <td onClick={(e) => e.stopPropagation()}>
                                            <Form.Check
                                                type="checkbox"
                                                checked={selectedRequestIds.has(request.id)}
                                                onChange={(e) => handleCheckboxChange(request.id, e)}
                                            />
                                        </td>
                                        <td>{usernameFor(request.requested_by)}</td>
                                        <td>{rumInventoryIdFor(request.rum)}</td>
                                        <td>{rumNameFor(request.rum)}</td>
                                        <td>{formattedDateTime(request.requested_on)}</td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </Table>
                </Tab>
                <Tab eventKey="rums" title="Rums">
                <Button variant="primary" onClick={addNewRum}>
                    Add New Rum
                </Button>
                <Table>
                    <thead>
                        <tr>
                            <th onClick= {()=>sortRums('name')}>Rum { rumSortField.current === 'name' ? (rumSortAscending.current ? "▲" : "▼") : "" }</th>
                            <th onClick= {()=>sortRums('inventory_id')}>RumID { rumSortField.current === 'inventory_id' ? (rumSortAscending.current ? "▲" : "▼") : "" }</th>
                            <th onClick= {()=>sortRums('price')}>Price { rumSortField.current === 'price' ? (rumSortAscending.current ? "▲" : "▼") : "" }</th>
                            <th onClick= {()=>sortRums('available')}>Available { rumSortField.current === 'available' ? (rumSortAscending.current ? "▲" : "▼") : "" }</th>
                            <th onClick= {()=>sortRums('date_added')}>Date Added { rumSortField.current === 'date_added' ? (rumSortAscending.current ? "▲" : "▼") : "" }</th>
                        </tr>
                    </thead>
                    <tbody>
                    {filteredRums.map( function(rum, i) {
                        return (
                            <tr key={i} onClick={() => showRum(i)}>
                                <td className={rum.is_immortal ? "immortal" : "regular-rum"}>{rum.bevager_name}</td>
                                <td>{rum.inventory_id}</td>
                                <td>{rum.price}</td>
                                <td>{rum.is_available ? "Yes" : "No"}</td>
                                <td>{formattedDateTime(rum.date_added)}</td>
                            </tr>
                        )
                    })}
                    </tbody>
                </Table>
                </Tab>
                <Tab eventKey="users" title="Users">
                <Table>
                    <thead>
                        <tr>
                            <th onClick= {()=>sortUsers('name')}>Name { userSortField.current === 'name' ? (userSortAscending.current ? "▲" : "▼") : "" }</th>
                            <th onClick= {()=>sortUsers('email')}>Email { userSortField.current === 'email' ? (userSortAscending.current ? "▲" : "▼") : "" }</th>
                            <th onClick= {()=>sortUsers('num_rums')}>Total Rums { userSortField.current === 'num_rums' ? (userSortAscending.current ? "▲" : "▼") : "" }</th>
                            <th onClick= {()=>sortUsers('num_immortals')}>Immortals { userSortField.current === 'num_immortals' ? (userSortAscending.current ? "▲" : "▼") : "" }</th>
                            <th onClick= {()=>sortUsers('level')}>Level { userSortField.current === 'level' ? (userSortAscending.current ? "▲" : "▼") : "" }</th>
                        </tr>
                    </thead>
                    <tbody>
                    {filteredUsers.map( function(user, i) {
                        return (
                            <tr key={i} onClick={() => showUser(i)}>
                                <td>{user.first_name} {user.last_name}</td>
                                <td>{user.email}</td>
                                <td>{user.num_rums}</td>
                                <td>{user.num_immortals}</td>
                                <td>{user.rumbustion_level}</td>
                            </tr>                            
                        )
                    })}
                    </tbody>
                </Table>
                </Tab>
            </Tabs>
        </Container>
    );
}

export { AdminPage };
